import React from "react";
import "./App.css";
import WebChatControl from "./WebChatControl";

import InfoForm, { IUserInfo as UserInfo } from "./InfoForm";
import BypassPrechatForm from "./BypassPrechatForm";
import { ChimeApi } from "./ChimeApi";

interface IAppProps { }
interface IAppState {
    chatStarted: boolean
}

interface IEndChatMessage {
    type: "chime:end-chat";
    requestId: string;
}

interface IEndChatResultMessage {
    type: "chime:end-chat-result";
    requestId: string;
    succeeded: boolean;
}

class App extends React.Component<IAppProps, IAppState> {
    private static DefaultTenantId = "00000000-0000-0000-0000-000000000000";
    private conversationId: string;
    private username: string;
    private userId: string;
    private token: string;
    private tenantId: string;
    private email: string;
    private guestName: string;
    private metadata: Map<string, string>;
    private tenantLocked: boolean;
    private styleSet: string;
    private playNotificationSound: string;
    private startChatAutomatically: string;    
    private pipelineContentItemId: string;
    private showPrechatForm: string;
    private chatEnded: boolean = false;

    constructor(props: IAppProps) {
        super(props);

        // Get query string parameters
        const params = new URLSearchParams(window.location.search);

        // Initialize metadata
        this.metadata = new Map<string, string>();
        const isTestChat = params.get("Session.IsTestChat")
        if (isTestChat?.toLowerCase() == "true") {
            this.metadata.set("Session.IsTestChat", "True");
        }

        // Add all WebClient.* parameters from query string
        params.forEach((value, key) => {
            if (key.startsWith("WebClient.")) {
                this.metadata.set(key, value);
            }
        });

        this.tenantId = (document.getElementById("tenantId") as HTMLInputElement)?.value;
        this.tenantLocked = this.tenantId != null;
        this.token = (document.getElementById("directLineSecret") as HTMLInputElement)?.value;
        this.userId = (document.getElementById("userId") as HTMLInputElement)?.value;
        this.username = (document.getElementById("username") as HTMLInputElement)?.value;
        this.conversationId = (document.getElementById("conversationId") as HTMLInputElement)?.value;
        
        // Set email from query string or fallback to DOM element value
        this.email = params.get('email') || (document.getElementById("Guest.Email") as HTMLInputElement)?.value;

        // Set guest name metadata
        this.guestName = params.get('name') || (document.getElementById("Guest.Name") as HTMLInputElement)?.value;
        if (this.guestName?.trim()) {
            this.metadata.set("Guest.Name", this.guestName);
        }

        this.styleSet = (document.getElementById("settings.styleSet") as HTMLInputElement)?.value;
        this.playNotificationSound = (document.getElementById("settings.playNotificationSound") as HTMLInputElement)?.value;   
        this.startChatAutomatically = (document.getElementById("settings.startChatAutomatically") as HTMLInputElement)?.value;
        this.showPrechatForm = (document.getElementById("settings.showPrechatForm") as HTMLInputElement)?.value;

        this.pipelineContentItemId = (document.getElementById("pipelineContentItemId") as HTMLInputElement)?.value;

        let chatStarted = false;
        if (this.token) { chatStarted = true; }
        this.state = { chatStarted: chatStarted }
        this.startChat = this.startChat.bind(this);
    }

    getThisStyleSet = () => {
        try { 
            return JSON.parse(this.styleSet)
        }
        catch (e) {
            console.log("Styleset was not able to be read in: ", e);
            return {}
        }
    }

    componentDidMount() {
        window.addEventListener('beforeunload', this.handleBeforeUnload);
        window.addEventListener('message', this.handleParentMessage);
    }

    componentWillUnmount() {
        window.removeEventListener('beforeunload', this.handleBeforeUnload);
        window.removeEventListener('message', this.handleParentMessage);
    }

    handleBeforeUnload = () => {
        this.leaveChat(true);
    }

    isEndChatMessage = (data: unknown): data is IEndChatMessage => {
        if (!data || typeof data !== "object") {
            return false;
        }

        const message = data as Partial<IEndChatMessage>;
        return message.type === "chime:end-chat" && typeof message.requestId === "string";
    }

    sendEndChatResult = (target: MessageEventSource, requestId: string, succeeded: boolean) => {
        const message: IEndChatResultMessage = {
            type: "chime:end-chat-result",
            requestId,
            succeeded
        };

        (target as WindowProxy).postMessage(message, window.location.origin);
    }

    handleParentMessage = async (event: MessageEvent) => {
        if (event.origin !== window.location.origin || event.source !== window.parent || !this.isEndChatMessage(event.data)) {
            return;
        }

        try {
            const result = await this.leaveChat(false);
            const succeeded = typeof result === "boolean" ? result : result.ok;
            this.sendEndChatResult(event.source, event.data.requestId, succeeded);
        } catch (error) {
            console.error("Unable to end chat", error);
            this.sendEndChatResult(event.source, event.data.requestId, false);
        }
    }

    leaveChat = (isSendBeacon = true): boolean | Promise<Response> => {
        if (this.chatEnded) {
            return true;
        }

        if (!this.conversationId) {
            return false;
        }

        console.log("Try leaveChat", this.conversationId);             
        const api = "./chat/EndChat?conversationId=" + this.conversationId;
        console.log("api route", api)
        if (isSendBeacon) {
            const queued = navigator.sendBeacon(api);
            this.chatEnded = queued;
            return queued;
        }

        return fetch(api, { method: 'POST', keepalive: true })
            .then(response => {
                this.chatEnded = response.ok;
                return response;
            });
    }

    render(): any {
        console.log(">> state", this.state);
        const { chatStarted } = this.state;
        if (!chatStarted) {
            if ( this.showPrechatForm == "True") {
                console.log(">> Show prechat form flow, showPrechatForm:", this.showPrechatForm)
                return (
                    <InfoForm
                        username={this.username}
                        tenantId={this.tenantId}
                        email={this.email}
                        startChat={this.startChat}
                    />
                );
            }
            else {
                console.log(">> No prechat form, showPrechatForm:", this.showPrechatForm)
                return (
                    <BypassPrechatForm 
                        tenantId={this.tenantId}
                        email={this.email}
                        username={this.username}
                        startChat={this.startChat}
                    />
                );
            }            
        }

        return (
            <WebChatControl
                token={this.token}
                conversationId={this.conversationId}
                tenantId={this.tenantId}
                userId={this.userId}
                username={this.username}
                email={this.email}
                metadata={this.metadata}
                styleSet={this.getThisStyleSet()}
                playNotificationSound={this.playNotificationSound}
                startChatAutomatically={this.startChatAutomatically}
                showPrechatForm={this.showPrechatForm}
            />
        );
    }

    async startChat(userInfo: UserInfo): Promise<any> {
        if (!this.tenantLocked) {
            this.tenantId = userInfo.tenantId;
        }
        this.username = userInfo.username;
        this.email = userInfo.email;
    
        // Merge userInfo.metadata into this.metadata instead of replacing it
        userInfo.metadata.forEach((value, key) => {
            this.metadata.set(key, value);
        });
    
        // Add additional session-related data to metadata
        this.metadata.set("Session.ReferrerUrl", document.referrer);
        this.metadata.set("Session.EntryPoint", "Web Client");
    
        if (this.pipelineContentItemId) {
            this.metadata.set("Session.PipelineContentItemId", this.pipelineContentItemId);
        }
    
        const json = await ChimeApi.getToken({
            tenantId: this.tenantId,
            name: this.username,
            email: this.email
        });
    
        console.log("logging json", json);
    
        this.conversationId = json.conversationId;
        this.token = json.token;
        this.username = json.user.name;
        this.email = json.user.email;
        this.userId = json.userId;
        this.tenantId = json.user.tenantId;
    
        (document.getElementById("conversationId") as HTMLInputElement).value = json.conversationId;
        (document.getElementById("username") as HTMLInputElement).value = json.user.name;
        (document.getElementById("Guest.Email") as HTMLInputElement).value = json.user.email;
        (document.getElementById("userId") as HTMLInputElement).value = json.userId;
    
        this.setState({ chatStarted: true });
    }
    
}

export default App;
